import React, { useEffect, useMemo } from "react";
import { isCorpTenant, isVesperTenant } from "@capone/common";
import { RouteComponentProps } from "react-router-dom";
import {
  ActionLink,
  CurrencyFormatters,
  TravelWalletSelectionWorkflow,
  getRestrictedATCText,
  getRestrictedUserTCTexts,
  filterOutSpendAndGetOffers,
  isSpendAndGetOffer,
  getCreditActivityGroups,
  formatPartialHistoryValidFromDate,
  getUpcomingCreditActivityItems,
} from "halifax";
import { CreditActivityDialog } from "@capone/portal";
import { CreditActivityDialog as VesperCreditActivityDialog } from "@vesper/portal";
import { CreditActivityDialog as CorpCreditActivityDialog } from "@capone-corporate/portal";
import { ENGAGED_OFFER_CTA, SelectedTravelOfferScreen } from "redmond";
import { TravelWalletSelectionConnectorProps } from "./container";
import { config } from "../../../../api/config";
import {
  AVAILABLE,
  C1_AUTO_SELECT_CARD_AT_CHECKOUT,
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";
import {
  APPLY_TRAVEL_CREDIT_TITLE,
  APPLY_TRAVEL_OFFER_TITLE,
  CREDIT_BALANCE_DESCRIPTION,
  CREDIT_HISTORY_BALANCE_TEXT,
  CREDIT_HISTORY_MODAL_LIST_HEADER,
  CREDIT_HISTORY_MODAL_SUBHEADER,
  getSelectedCreditDescription,
  getSelectedOfferDescription,
  TRAVEL_CREDIT_AND_OFFER_TOTAL_PAYMENT_TEXT,
  TRAVEL_CREDIT_SUBTITLE,
  TRAVEL_CREDIT_TOTAL_PAYMENT_TEXT,
  TRAVEL_OFFER_SUBTITLE,
  TRAVEL_OFFER_TOTAL_PAYMENT_TEXT,
  VIEW_CREDIT_HISTORY_CTA,
} from "./textConstants";
import { ClientContext } from "../../../../App";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { DO_NOT_APPLY_REWARDS_KEY } from "../../reducer";

export interface ITravelWalletSelectionProps
  extends TravelWalletSelectionConnectorProps,
    RouteComponentProps {
  className?: string;
  isMobile?: boolean;
  disabled?: boolean;
  isAnnualTravelCreditsExperiment?: boolean;
  isCreditAndOfferStackingExperimentV1?: boolean;
  isTravelSale?: boolean;
}

const getCreditActivityDialogByTenant = () => {
  if (isCorpTenant(config.TENANT)) return CorpCreditActivityDialog;
  if (isVesperTenant(config.TENANT)) return VesperCreditActivityDialog;
  return CreditActivityDialog;
};

export const TravelWalletSelection = (props: ITravelWalletSelectionProps) => {
  const {
    offers,
    offerToApply,
    setOfferToApply,
    credits,
    creditBreakdown,
    creditsToApply,
    setCreditToApply,
    disabled,
    isTravelCreditPaymentOnly,
    isTravelWalletOfferPaymentOnly,
    isStackedTravelWalletPaymentOnly,
    isMobile,
    creditsHistoryTransaction,
    selectedPaymentMethodId,
    selectedRewardsPaymentAccountId,
    setSelectedRewardsPaymentTotal,
    resetPaymentCardSelectedAccounts,
    bestOverallOffer,
    rewardsAccounts,
    isAnnualTravelCreditsExperiment,
    isCreditAndOfferStackingExperimentV1,
    maxApplicableTravelWalletCredit,
    isTravelSale,
    userDeselectedCredits,
    userDeselectedOffers,
    setUserDeselectedCredits,
    setUserDeselectedOffers,
    ineligibleCreditsBreakdown,
    partialHistoryValidFromTime,
  } = props;

  const firstName =
    React.useContext(ClientContext).sessionInfo?.userInfo?.firstName || "";

  const filteredOffers = useMemo(
    () => (offers ? filterOutSpendAndGetOffers(offers) : undefined),
    [offers],
  );

  const filteredBestOverallOffer = useMemo(
    () =>
      bestOverallOffer && isSpendAndGetOffer(bestOverallOffer.descriptions)
        ? undefined
        : bestOverallOffer,
    [bestOverallOffer],
  );

  if (!filteredOffers) return null;

  const ResolvedCreditActivityDialog = getCreditActivityDialogByTenant();

  const expState = useExperiments();
  const autoSelectExperimentOn =
    getExperimentVariant(
      expState.experiments,
      C1_AUTO_SELECT_CARD_AT_CHECKOUT,
    ) === AVAILABLE;

  useEffect(() => {
    if (credits && !userDeselectedCredits) {
      if (!isCorpTenant(config.TENANT) || !creditsToApply) {
        setCreditToApply(credits);
      }
    }
  }, [credits, userDeselectedCredits]);

  useEffect(() => {
    const hasRealRewardsSelection =
      selectedRewardsPaymentAccountId &&
      selectedRewardsPaymentAccountId !== DO_NOT_APPLY_REWARDS_KEY;

    if (selectedPaymentMethodId || hasRealRewardsSelection) {
      if (!autoSelectExperimentOn) {
        resetPaymentCardSelectedAccounts();
      }
      if (!isCorpTenant(config.TENANT)) {
        setSelectedRewardsPaymentTotal("", null, null);
      }
    }
  }, [offerToApply, creditsToApply, autoSelectExperimentOn]);

  useEffect(() => {
    if (filteredBestOverallOffer && !userDeselectedOffers) {
      if (!isCorpTenant(config.TENANT) || !offerToApply) {
        setOfferToApply(filteredBestOverallOffer);
      }
    }
  }, [filteredBestOverallOffer, userDeselectedOffers]);

  return (
    <TravelWalletSelectionWorkflow
      isMobile={isMobile}
      offersAvailable={filteredOffers || []}
      creditsAvailable={credits}
      offerToApply={offerToApply}
      creditsToApply={creditsToApply}
      setOfferToApply={(offer) => {
        if (!offer && offerToApply) {
          setUserDeselectedOffers(true);
        } else if (offer) {
          setUserDeselectedOffers(false);
        }
        setOfferToApply(offer);
      }}
      setCreditToApply={(credit) => {
        if (!credit && creditsToApply) {
          setUserDeselectedCredits(true);
        } else if (credit) {
          setUserDeselectedCredits(false);
        }
        setCreditToApply(credit);
      }}
      titles={{
        creditTitle: APPLY_TRAVEL_CREDIT_TITLE,
        creditSubtitle: TRAVEL_CREDIT_SUBTITLE,
        offersTitle: APPLY_TRAVEL_OFFER_TITLE,
        offersSubtitle: TRAVEL_OFFER_SUBTITLE,
      }}
      getCreditDescription={getSelectedCreditDescription}
      getOfferDescription={getSelectedOfferDescription}
      disabled={disabled}
      totalCoverageTexts={{
        creditsCoverTotalText: isTravelCreditPaymentOnly
          ? TRAVEL_CREDIT_TOTAL_PAYMENT_TEXT
          : undefined,
        offerCoversTotalText: isTravelWalletOfferPaymentOnly
          ? TRAVEL_OFFER_TOTAL_PAYMENT_TEXT
          : undefined,
        travelWalletCoverTotalText: isStackedTravelWalletPaymentOnly
          ? TRAVEL_CREDIT_AND_OFFER_TOTAL_PAYMENT_TEXT
          : undefined,
      }}
      isTravelWalletCreditPaymentOnly={isTravelCreditPaymentOnly}
      isTravelWalletOfferPaymentOnly={isTravelWalletOfferPaymentOnly}
      maxApplicableCreditAmount={maxApplicableTravelWalletCredit}
      travelCreditHistoryProps={
        creditsHistoryTransaction?.length
          ? {
              modalContentProps: {
                header: CREDIT_BALANCE_DESCRIPTION(firstName),
                balanceText: CREDIT_HISTORY_BALANCE_TEXT(
                  CurrencyFormatters.getSymbol(credits?.amount.currency),
                  CurrencyFormatters.get(credits?.amount.currency).format(
                    (credits?.amount?.amount && credits?.amount?.amount * -1) ||
                      0,
                  ),
                ),
                subHeader: CREDIT_HISTORY_MODAL_SUBHEADER,
                creditListHeader: CREDIT_HISTORY_MODAL_LIST_HEADER,
              },
              viewHistoryCTAText: VIEW_CREDIT_HISTORY_CTA,
              creditTransactionsHistory: creditsHistoryTransaction,
              credit: credits,
              creditBreakdown,
              rewardsAccounts,
            }
          : undefined
      }
      creditActivityDialogSlot={
        creditsHistoryTransaction?.length ? (
          <ResolvedCreditActivityDialog
            trigger={<ActionLink content={VIEW_CREDIT_HISTORY_CTA} />}
            groups={getCreditActivityGroups(
              creditsHistoryTransaction,
              creditBreakdown,
            )}
            upcomingItems={getUpcomingCreditActivityItems(creditBreakdown)}
            partialHistoryValidFromDate={formatPartialHistoryValidFromDate(
              partialHistoryValidFromTime,
            )}
          />
        ) : undefined
      }
      isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
      isCreditAndOfferStackingExperimentV1={
        isCreditAndOfferStackingExperimentV1
      }
      restrictedATCText={
        getRestrictedATCText(rewardsAccounts, creditBreakdown) ||
        getRestrictedUserTCTexts(rewardsAccounts, creditBreakdown)?.[0]
      }
      isTravelSale={isTravelSale}
      onClickOfferTooltip={(offer) => {
        trackEvent({
          eventName: ENGAGED_OFFER_CTA,
          properties: {
            location: SelectedTravelOfferScreen.FLIGHT_CHECKOUT,
            entry_type: "tooltip",
            funnel: offer.funnels.join(","),
          },
          encryptedProperties: [
            offer.trackingPropertiesV2?.encryptedProperties ?? "",
          ],
        });
      }}
      ineligibleCreditsBreakdown={ineligibleCreditsBreakdown}
    />
  );
};
